// FILE: src/pages/AdminCompliaceUserDetailPage.jsx
// One student's compliance record (plan WP-8, MTO 11.1.2-3): result, time,
// chapters and homelinks with dates and scores, and their support requests.
// B-8: the progress tables now get *this student's* progress
// (`chaptersViewedProp`, `homelinksViewed`) and the course's chapters and
// homelinks from the admin lesson list, not the signed-in admin's own data.
// WP-7 (B-8): the record shows the school's registration number, the licence
// and the enrolment dates, and the credited time comes from the daily
// ledger (GET /api/admin/users/:id progressSummary) when it is available.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Stack, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import { clearUserDetails, fetchUserCompliance } from "../features/complianceSlice";
import { fetchAdminResources } from "../features/resourceSlice";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section, StatCard } from "../components/ui";
import { CourseStatusChip, ResponsiveTable, TicketStatusChip } from "../components/admin/AdminBits";
import { formatDate, formatDateTime } from "../components/admin/format";
import CourseProgressTable from "../components/CourseProgressTable";
import HomelinksTable from "../components/HomelinksTable";
import { formatDuration } from "../utils/timeFormat";
import fetchWithAuth from "../utils/fetchWithAuth";
import { errorCode, messageFor } from "../i18n/errors";
import { licenceClassLabel, ticketCategoryLabel, userStatusLabel } from "../i18n/labels";
import usePermissions from "../hooks/usePermissions";

const RESULT_KEY = { PASSED: "completed", "IN PROGRESS": "in-progress", "NOT STARTED": "not-started" };
const byOrder = (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0);
const sumSec = (arr) => (Array.isArray(arr) ? arr.reduce((t, r) => t + (Number(r?.timeSpentSec) || 0), 0) : 0);

export default function AdminCompliaceUserDetailPage() {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const { userDetails, loading, error } = useSelector((state) => state.compliance);
  const { list: resources } = useSelector((state) => state.resources);

  // Until this page has asked, an older failure in the slice isn't ours.
  const [asked, setAsked] = useState(false);
  const load = useCallback(() => {
    setAsked(true);
    dispatch(fetchUserCompliance(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    load();
    return () => {
      dispatch(clearUserDetails());
    };
  }, [dispatch, load]);

  useEffect(() => {
    dispatch(fetchAdminResources());
  }, [dispatch]);

  // Credited time from the daily ledger (one authoritative figure, MTO 11.1.2).
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    let live = true;
    setLedger(null);
    fetchWithAuth(`/api/admin/users/${encodeURIComponent(userId)}`)
      .then((u) => {
        if (live) setLedger(u?.progressSummary?.creditedMinutes || null);
      })
      .catch(() => {
        /* the record's own figures are shown instead */
      });
    return () => {
      live = false;
    };
  }, [userId]);

  const { chapters, homelinks } = useMemo(() => {
    const all = Array.isArray(resources) ? resources : [];
    return {
      chapters: all.filter((r) => r.resourceType === "CHAPTER").sort(byOrder),
      homelinks: all.filter((r) => r.resourceType === "HOMELINK").sort(byOrder)
    };
  }, [resources]);

  const breadcrumbs = [{ label: "Compliance reports", to: "/admin/compliance" }, { label: "Student record" }];

  if (!userDetails) {
    if (asked && error && !loading) {
      const missing = ["USER_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
      return (
        <Box>
          <PageHeader title="Student record" breadcrumbs={breadcrumbs} />
          {missing ? (
            <EmptyState
              title={messageFor("USER_NOT_FOUND")}
              description="Go back to the compliance reports and choose a student."
              action={
                <Button component={RouterLink} to="/admin/compliance" variant="contained">
                  Back to compliance reports
                </Button>
              }
            />
          ) : (
            <ErrorState error={error} title="We couldn't load this record" onRetry={load} />
          )}
        </Box>
      );
    }
    return <LoadingState variant="page" label="Loading the student's record…" />;
  }

  const d = userDetails;
  const progress = d.progress || {};
  const chaptersViewed = Array.isArray(progress.chaptersViewed) ? progress.chaptersViewed : [];
  const homelinksViewed = Array.isArray(progress.homelinksViewed) ? progress.homelinksViewed : [];
  const resultKey = RESULT_KEY[d.finalResult] || "not-started";
  const chapterSec = ledger ? Math.round((Number(ledger.core) || 0) * 60) : sumSec(chaptersViewed);
  const homelinkSec = ledger ? Math.round((Number(ledger.flex) || 0) * 60) : sumSec(homelinksViewed);
  const totalSec = ledger ? Math.round((Number(ledger.total) || 0) * 60) : chapterSec + homelinkSec;
  const chaptersDone = d.modules?.completed ?? chaptersViewed.filter((c) => c.completed).length;
  const homelinksDone = d.homelinks?.completed ?? homelinksViewed.filter((h) => h.completed).length;
  const chapterTotal = chapters.length || d.modules?.total || 0;
  const homelinkTotal = homelinks.length || d.homelinks?.total || 0;
  const tickets = Array.isArray(d.tickets) ? d.tickets : [];
  const name = d.fullName || "Student";

  return (
    <Box>
      <PageHeader
        title={name}
        eyebrow="Compliance record"
        breadcrumbs={[{ label: "Compliance reports", to: "/admin/compliance" }, { label: name }]}
        description="Time credited, lessons completed, results and support requests for this student."
        actions={
          <>
            {can("students.view") && (
              <Button component={RouterLink} to={`/admin/users/${userId}`} variant="outlined" startIcon={<PersonOutlineIcon />}>
                Student page
              </Button>
            )}
            {can("records.export") && (
              <Button
                component={RouterLink}
                to={`/admin/users/${userId}/records`}
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Download record
              </Button>
            )}
          </>
        }
      >
        <Box sx={{ mt: 2 }}>
          <CourseStatusChip status={resultKey} />
        </Box>
      </PageHeader>

      {d.untimed && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          This is an untimed (reviewer) account. It isn't part of the compliance set and its time isn't counted.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
          mb: 2.5
        }}
      >
        <StatCard
          icon={FlagOutlinedIcon}
          label="Course result"
          value={userStatusLabel(resultKey)}
          note={d.completedDate ? `Finished ${formatDate(d.completedDate)}` : "Not finished yet"}
        />
        <StatCard
          icon={ScheduleOutlinedIcon}
          label="Time credited"
          value={formatDuration(totalSec)}
          note={`${formatDuration(chapterSec)} chapters · ${formatDuration(homelinkSec)} homelinks`}
        />
        <StatCard
          icon={MenuBookOutlinedIcon}
          label="Chapters completed"
          value={`${chaptersDone} of ${chapterTotal}`}
          progress={chapterTotal ? (chaptersDone / chapterTotal) * 100 : 0}
          progressLabel="Chapters completed"
        />
        <StatCard
          icon={HomeWorkOutlinedIcon}
          label="Homelinks completed"
          value={`${homelinksDone} of ${homelinkTotal}`}
          progress={homelinkTotal ? (homelinksDone / homelinkTotal) * 100 : 0}
          progressLabel="Homelinks completed"
        />
      </Box>

      <Stack spacing={2.5}>
        <Section id="record" title="Record">
          <DetailList
            items={[
              { label: "Full name", value: name },
              { label: "Registration number", value: d.registrationNumber || "Not assigned yet" },
              {
                label: "Licence",
                value:
                  [d.licenseClass ? licenceClassLabel(d.licenseClass) : null, d.licenseNumber || d.registrationNo]
                    .filter(Boolean)
                    .join(" · ") || "Not given"
              },
              { label: "Added on", value: formatDate(d.startDate) },
              { label: "Enrolment started", value: d.enrolmentStartedAt ? formatDate(d.enrolmentStartedAt) : "Not started" },
              { label: "Enrolment ends", value: d.enrolmentExpiresAt ? formatDate(d.enrolmentExpiresAt) : "—" },
              { label: "Completed on", value: d.completedDate ? formatDate(d.completedDate) : "Not finished yet" },
              {
                label: "Average final score",
                value: resultKey === "completed" && Number.isFinite(Number(d.totalScore)) ? `${d.totalScore}%` : "Not finished yet"
              },
              { label: "Course result", value: userStatusLabel(resultKey) }
            ]}
          />
        </Section>

        <Section id="chapters" title="Chapters" description="In-class lessons, in course order.">
          <CourseProgressTable chaptersProp={chapters} chaptersViewedProp={chaptersViewed} viewOnly />
        </Section>

        <Section id="homelinks" title="Homelinks" description="Flexible-hour activities, in course order.">
          <HomelinksTable homelinksProp={homelinks} homelinksViewedProp={homelinksViewed} viewOnly />
        </Section>

        <Section id="support" title="Support requests" description="Requests this student sent, newest first.">
          {tickets.length === 0 ? (
            <EmptyState compact title="No support requests" description="This student hasn't asked for help." />
          ) : (
            <ResponsiveTable label="Support requests from this student" minWidth={640}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Kind of help
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Status
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Opened
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets
                  .slice()
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((t) => (
                    <TableRow key={t._id}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                        {ticketCategoryLabel(t.category)}
                      </TableCell>
                      <TableCell>
                        <TicketStatusChip status={t.status} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(t.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          component={RouterLink}
                          to={`/admin/support/${t._id}`}
                          variant="outlined"
                          aria-label={`View ${ticketCategoryLabel(t.category).toLowerCase()} request from ${formatDate(t.createdAt)}`}
                        >
                          View request
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </ResponsiveTable>
          )}
        </Section>
      </Stack>
    </Box>
  );
}
